/**
 * Runtime configuration loaded from /config.js
 *
 * - Development: docker-compose mounts config.js with local values
 * - Production: Kubernetes ConfigMap provides environment-specific values
 *
 * FAIL LOUDLY: No fallbacks - missing config throws immediately
 */

/** Which install this is: the shared cloud, or a customer's own cluster. */
export type Deployment = 'cloud' | 'enterprise'

interface RuntimeConfig {
  DEPLOYMENT?: Deployment
  KEYCLOAK_AUTHORITY: string
  KEYCLOAK_CLIENT_ID: string
  // The API base URL is NOT runtime config: VITE_API_URL is baked in at
  // build time and read by src/lib/env.ts.
  // Written by Pulumi only when the stack enables analytics (TRA-1105).
  // Absent means analytics is off. src/lib/analytics.ts is the only reader.
  POSTHOG_KEY?: string
  POSTHOG_HOST?: string
}

declare global {
  interface Window {
    __CONFIG__?: RuntimeConfig
  }
}

function getConfig(): RuntimeConfig {
  const runtimeConfig = window.__CONFIG__

  if (!runtimeConfig) {
    throw new Error(
      'Runtime config not found. Ensure /config.js is loaded before the app and sets window.__CONFIG__'
    )
  }

  return runtimeConfig
}

export const config = getConfig()

// Validate required config (called by AuthProvider on mount)
export function validateConfig(): void {
  if (!config.KEYCLOAK_AUTHORITY) {
    throw new Error('KEYCLOAK_AUTHORITY is required in window.__CONFIG__')
  }
  if (!config.KEYCLOAK_CLIENT_ID) {
    throw new Error('KEYCLOAK_CLIENT_ID is required in window.__CONFIG__')
  }
}

// Pulumi and the platform chart write DEPLOYMENT on enterprise installs
// only; absent means cloud.
function readDeployment(value: unknown): Deployment {
  if (value === undefined || value === 'cloud') return 'cloud'
  if (value === 'enterprise') return 'enterprise'
  throw new Error('DEPLOYMENT must be cloud or enterprise')
}
export const DEPLOYMENT: Deployment = readDeployment(config.DEPLOYMENT)
// cloud adds the Stripe billing worker; enterprise has none and hides Billing.
export const BILLING_ENABLED = DEPLOYMENT === 'cloud'
// The team alert callback is a Slack-or-webhook POST that only our cloud
// jobs send, so enterprise hides Notifications (TRA-1593).
export const NOTIFICATIONS_ENABLED = DEPLOYMENT === 'cloud'
